import React from 'react';
import { useTranslation } from 'react-i18next';
import { Heading, Card, Text } from '@puppet/react-components';
import { useSelector } from 'react-redux';
import { selectUser } from '@state/session/selectors';
import PageLayout from '@components/PageLayout';
import { AuthTokenV1 } from '@utils/api/cd4pe';
import UserDetailsCard from '@codeDelivery/components/UserDetailsCard';
import { UserV1 } from '@puppet/cd4pe-client-ts';
import Breadcrumbs, {
  BreadcrumbsType,
} from '@components/Breadcrumbs/Breadcrumbs';
import AccessTokenForm from './components/AccessTokenForm';
import AccessTokenTable from './components/AccessTokenTable';
import RevokeTokenDialog from './components/RevokeTokenDialog';

interface ProfileProps {
  user?: UserV1;
  breadcrumbs?: BreadcrumbsType;
}

const Profile = ({ user, breadcrumbs }: ProfileProps) => {
  const { t } = useTranslation('codeDelivery');
  const loggedInUser = useSelector(selectUser) as UserV1;
  const userToView = user ?? loggedInUser;
  const [tokenToRevoke, setTokenToRevoke] = React.useState<AuthTokenV1 | null>(
    null,
  );

  const onRevokeDialogClose = () => {
    setTokenToRevoke(null);
  };

  const onRevokeTokenHandler = (token: AuthTokenV1) => {
    setTokenToRevoke(token);
  };

  return (
    <>
      <PageLayout className="profile-page">
        <PageLayout.Header>
          {breadcrumbs && (
            <PageLayout.Breadcrumbs>
              <Breadcrumbs breadcrumbs={breadcrumbs} />
            </PageLayout.Breadcrumbs>
          )}
          <Heading>{t('profile.header')}</Heading>
        </PageLayout.Header>
        <PageLayout.Content>
          <UserDetailsCard
            loggedInUser={loggedInUser}
            userToView={userToView}
          />
          {userToView.id === loggedInUser.id ? (
            <Card className="profile-page-token-card">
              <div className="profile-page-token-card__description">
                <Heading as="h3">
                  {t('profile.tokens.description.title')}
                </Heading>
                <Text>{t('profile.tokens.description.content')}</Text>
              </div>
              <div className="profile-page-token-card__token-form">
                <div className="profile-page-token-card__token-form__description">
                  <Heading as="h5">{t('profile.tokens.form.title')}</Heading>
                  <Text>{t('profile.tokens.form.description')}</Text>
                </div>
                <AccessTokenForm />
              </div>
              <div className="profile-page-token-card__divider" />
              <div className="profile-page-token-card__token-table">
                <Heading as="h5">{t('profile.tokens.table.title')}</Heading>
                <AccessTokenTable
                  user={userToView}
                  onRevokeToken={onRevokeTokenHandler}
                />
              </div>
            </Card>
          ) : (
            <Card className="standalone-token-table">
              <Heading as="h3">{t('profile.tokens.table.title')}</Heading>
              <AccessTokenTable
                user={userToView}
                onRevokeToken={onRevokeTokenHandler}
              />
            </Card>
          )}
        </PageLayout.Content>
      </PageLayout>
      {tokenToRevoke && (
        <RevokeTokenDialog
          token={tokenToRevoke}
          onClose={onRevokeDialogClose}
        />
      )}
    </>
  );
};

export default Profile;
